import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useRetrieveProjectDetailsConfiguration,
} from "@planzone/view-models";
import { useRoles } from "@scenes/safeguard";
import { Language } from "@common/language";
import { Props } from "./types";
import styles from "./styles.scss";

function SecondaryCriteria({ project, criteriaForms }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { configuration } = useRetrieveProjectDetailsConfiguration(planzone);
  const roles = useRoles({ project });
  const profile =
    configuration.find((p) =>
      p.roles.find((role) =>
        roles.find((r) => r.uuid === role || r.name === role),
      ),
    ) || configuration.length > 0
      ? configuration[0]
      : {
          name: "",
          roles: [],
          elements: [],
        };
  const columns = profile.elements.filter(
    ({ key }) =>
      !!template.map(({ uuid }) => uuid).find((criteria) => criteria === key),
  );

  return (
    <Language>
      {({ currentLanguage }) => (
        <div className={styles.secondaryCriteria}>
          {columns.map(({ key, type, elements }) => {
            const criteria = template.find(({ uuid }) => uuid === key);

            return type === "section" ? (
              elements.map((element) => {
                const criteria = template.find(
                  ({ uuid }) => uuid === element.key,
                );

                return (
                  <div key={element.key} className={styles.row}>
                    <div className={styles.label}>
                      <p
                        className={classNames({
                          [styles.required]:
                            !!criteria && criteria.attr.required === "true",
                        })}
                      >
                        {t(`projectSection.columnNames.${key}`, key)}:{" "}
                        {!!criteria
                          ? (criteria.labels[currentLanguage]
                              ? criteria.labels[currentLanguage]
                              : criteria.name) +
                            (criteria.attr.required === "true" ? "*" : "")
                          : t(
                              `projectSection.columnNames.${element.key}`,
                              element.key,
                            )}
                      </p>
                    </div>
                    <div className={styles.form}>
                      {criteriaForms[element.key as keyof typeof criteria]}
                    </div>
                  </div>
                );
              })
            ) : (
              <div key={key} className={styles.row}>
                <div className={styles.label}>
                  <p
                    className={classNames({
                      [styles.required]:
                        !!criteria && criteria.attr.required === "true",
                    })}
                  >
                    {!!criteria
                      ? criteria.labels[currentLanguage]
                        ? criteria.labels[currentLanguage]
                        : criteria.name
                      : t(`projectSection.columnNames.${key}`, key)}
                  </p>
                </div>
                <div className={styles.form}>
                  {criteriaForms[key as keyof typeof criteria]}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Language>
  );
}

export { SecondaryCriteria };
